import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import './dotglobal-diff.js';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

/**
 * Commit detail view: a single commit's message, metadata (author, verification, parents)
 * and full diff via the shared `dotglobal-diff` viewer. Mirrors the Claude Design
 * `changes.jsx` CommitDetail, wired to the real public commit API. Emits
 * `dotglobal-navigate` and `dotglobal-toast`.
 */
@customElement('dotglobal-commit-detail')
export class DotglobalCommitDetail extends DeesElement {
  @property({ type: Object })
  public accessor data: interfaces.IReq_PublicCommits['response'] | null = null;

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; }
      .empty { padding: 48px 20px; text-align: center; color: var(--text-muted); }

      .commit-head {
        padding-bottom: 18px;
        margin-bottom: 20px;
        border-bottom: 1px solid var(--border-default);
      }
      .commit-head h1 {
        margin: 0;
        font-family: var(--font-display);
        font-size: 22px;
        line-height: 1.25;
        letter-spacing: -0.02em;
      }
      .commit-body {
        margin: 12px 0 0;
        padding: 12px 14px;
        border: 1px solid var(--border-default);
        border-radius: 6px;
        background: var(--bg-secondary);
        color: var(--text-secondary);
        font-family: var(--font-mono);
        font-size: 12.5px;
        line-height: 1.6;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
      .commit-meta {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
        margin-top: 14px;
      }
      .avatar { width: 26px; height: 26px; font-size: 10px; cursor: pointer; }
      .commit-meta .who { font-size: 13.5px; font-weight: 600; cursor: pointer; }
      .commit-meta .who:hover { color: var(--accent); }
      .vbadge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11px;
        font-weight: 600;
        padding: 2px 8px;
        border-radius: 999px;
      }
      .vbadge.ok { color: var(--success); background: color-mix(in srgb, var(--success) 14%, transparent); }
      .vbadge.no { color: var(--text-muted); border: 1px solid var(--border-default); }
      .shaline { display: inline-flex; align-items: center; gap: 7px; }
      .shaline .label { color: var(--text-muted); font-size: 12px; }
      .sha {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-family: var(--font-mono);
        font-size: 12px;
        color: var(--accent);
        cursor: pointer;
        padding: 2px 7px;
        border-radius: 5px;
      }
      .sha:hover { background: color-mix(in srgb, var(--accent) 12%, transparent); }
      .parents {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 12px;
        color: var(--text-muted);
        font-size: 12.5px;
      }
      .parents .sha { color: var(--accent); }
    `,
  ];

  public render(): TemplateResult {
    const data = this.data;
    const repo = data?.repo;
    if (!data || !repo) {
      return html`<div class="page"><div class="empty">Repository not found.</div></div>`;
    }
    const commit = data.selectedCommit;
    if (!commit) {
      return html`
        <div class="page">
          <button class="btn sm ghost" style="margin-bottom: 14px" @click=${() => this.navigate(this.commitsHash(repo))}>${this.icon('ChevronLeft', 14)} Commits</button>
          <div class="empty">This commit was not returned by the public API.</div>
        </div>
      `;
    }
    return html`
      <div class="page">
        <button class="btn sm ghost" style="margin-bottom: 14px" @click=${() => this.navigate(this.commitsHash(repo))}>${this.icon('ChevronLeft', 14)} Commits</button>

        <div class="commit-head">
          <h1>${commit.title || commit.shortSha}</h1>
          ${commit.body ? html`<pre class="commit-body">${commit.body}</pre>` : ''}
          <div class="commit-meta">
            <span class="avatar" @click=${() => this.openAuthor(commit)}>
              ${this.initials(commit.authorName || commit.authorLogin)}
              ${commit.authorAvatarUrl
                ? html`<img src=${commit.authorAvatarUrl} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
                : ''}
            </span>
            <span class="who" @click=${() => this.openAuthor(commit)}>${commit.authorLogin || commit.authorName || 'unknown'}</span>
            <span class="muted" style="font-size: 13px">committed ${this.timeAgo(commit.createdAt)}</span>
            <span class="grow"></span>
            ${commit.verified
              ? html`<span class="vbadge ok" title="Verified signature">${this.icon('ShieldCheck', 12)} Verified</span>`
              : html`<span class="vbadge no" title="Unsigned commit">Unverified</span>`}
            <span class="shaline">
              <span class="label">commit</span>
              <button class="sha" @click=${() => this.copy(commit.sha, 'Full commit SHA copied')}>${commit.shortSha} ${this.icon('Copy', 12)}</button>
            </span>
          </div>
          ${commit.parents.length
            ? html`
                <div class="parents">
                  ${this.icon('GitCommitHorizontal', 13)}
                  <span>${commit.parents.length} parent${commit.parents.length === 1 ? '' : 's'}</span>
                  ${commit.parents.map((parent) => html`<button class="sha" @click=${() => this.navigate(this.commitHash(repo, parent))}>${parent}</button>`)}
                </div>
              `
            : ''}
        </div>

        <dotglobal-diff
          .files=${commit.files}
          .truncated=${commit.diffTruncated}
          .defaultView=${'unified'}
        ></dotglobal-diff>
      </div>
    `;
  }

  private commitsHash(repoArg: interfaces.IPublicRepo): string {
    return `#/repo/${encodeURIComponent(repoArg.org)}/${encodeURIComponent(repoArg.name)}/commits`;
  }

  private commitHash(repoArg: interfaces.IPublicRepo, shaArg: string): string {
    return `#/repo/${encodeURIComponent(repoArg.org)}/${encodeURIComponent(repoArg.name)}/commits/${encodeURIComponent(shaArg)}`;
  }

  private openAuthor(commitArg: interfaces.IPublicCommitDetail): void {
    if (commitArg.authorLogin) {
      this.navigate(`#/user/${encodeURIComponent(commitArg.authorLogin)}`);
    }
  }

  private async copy(textArg: string, messageArg: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(textArg);
      this.toast(messageArg);
    } catch {
      this.toast('Could not copy to clipboard');
    }
  }

  private navigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }

  private toast(messageArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-toast', { detail: { message: messageArg }, bubbles: true, composed: true }));
  }

  private icon(nameArg: string, sizeArg = 15): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${sizeArg}></dees-icon>`;
  }

  private initials(labelArg: string): string {
    return (labelArg || 'fg').split(/[.\s/-]+/).filter(Boolean).slice(0, 2).map((partArg) => partArg[0]?.toUpperCase()).join('') || 'fg';
  }

  private timeAgo(timestampArg: string): string {
    if (!timestampArg) {
      return 'unknown';
    }
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return 'unknown';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 60) {
      return `${days}d ago`;
    }
    return `${Math.round(days / 30)}mo ago`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-commit-detail': DotglobalCommitDetail;
  }
}
